/** White-labelling (B08-001 live preview + contrast, B08-002 custom domain, B08-003 true descriptor, B08-004 hide branding). */
import { useState, type CSSProperties, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { BRAND_SURFACES, CNAME_TARGET, brandTokens, trueBillingDescriptor, type BrandSurface } from '@qm/contracts/workspace';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Notice, Screen, Tabs } from '../kit/ui';
import type { BrandView } from '../api';
import { brandProblems, canHideBranding, descriptorError, domainStep, type BrandForm } from '../lib/logic';
import { usePartnerApi } from './layout';
import { urlFieldProps, withHttps } from '../../../lib/url';

export function BrandingPage() {
  const { t } = useSurface();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'brand';
  return (
    <Screen title={t('portal.partners.brand.title')} testId="branding">
      <Tabs label={t('portal.partners.brand.title')} active={tab} onSelect={(v) => setSp({ tab: v })} tabs={[
        { id: 'brand', label: t('portal.partners.brand.tab.brand') }, { id: 'domain', label: t('portal.partners.brand.tab.domain') }, { id: 'billing', label: t('portal.partners.brand.tab.billing') },
      ]} />
      {tab === 'domain' ? <DomainsPanel /> : tab === 'billing' ? <DescriptorPanel /> : <BrandPanel />}
    </Screen>
  );
}

function BrandPanel() {
  const api = usePartnerApi();
  const brand = useResource('brand', api.brand);
  return <Loadable resource={brand}>{(b) => <BrandEditor initial={b} onSaved={brand.reload} />}</Loadable>;
}

export function BrandEditor({ initial, onSaved }: { initial: BrandView; onSaved: () => void }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [f, setF] = useState<BrandForm>({ portalName: initial.portalName ?? '', logoUrl: initial.logoUrl ?? '', accentColor: initial.accentColor ?? '', supportEmail: initial.supportEmail ?? '' });
  const [hide, setHide] = useState(initial.hideQuixoBranding);
  const save = useAction(api.saveBrand);
  const problems = brandProblems(f);
  const contrast = problems.find((p) => p.key === 'contrast');
  const hideBlocked = hide && !canHideBranding(initial);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (problems.length || hideBlocked) return;
    const r = await save.run({
      ...(f.portalName ? { portalName: f.portalName } : {}), ...(withHttps(f.logoUrl) ? { logoUrl: withHttps(f.logoUrl) } : {}),
      ...(f.accentColor ? { accentColor: f.accentColor.toUpperCase() } : {}), ...(f.supportEmail ? { supportEmail: f.supportEmail } : {}), hideQuixoBranding: hide,
    });
    if (r) onSaved();
  };
  const input = (k: keyof BrandForm, label: string, type = 'text') => {
    const p = problems.find((x) => x.field === k);
    return <Field label={label} error={p ? t(`portal.partners.brand.error.${p.key}`) : null}>{(id) => <input id={id} name={k} type={type} value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} {...(type === 'url' ? urlFieldProps(f[k], (v) => setF((x) => ({ ...x, [k]: v }))) : {})} />}</Field>;
  };
  return (
    <div className="qx-grid">
      <Panel>
        <form onSubmit={submit}>
          {input('portalName', t('portal.partners.brand.portalName'))}
          {input('logoUrl', t('portal.partners.brand.logo'), 'url')}
          {input('accentColor', t('portal.partners.brand.accent'))}
          {contrast?.suggestion ? (
            <Notice tone="warn" testId="contrast-blocked">
              {t('portal.partners.brand.contrastSuggestion', { color: contrast.suggestion })}{' '}
              <Button onClick={() => setF((x) => ({ ...x, accentColor: contrast.suggestion! }))}>{t('portal.partners.brand.useSuggestion')}</Button>
            </Notice>
          ) : null}
          {input('supportEmail', t('portal.partners.brand.supportEmail'), 'email')}
          <Checkbox name="hide" label={t('portal.partners.brand.hide')} checked={hide} onChange={setHide} />
          {hideBlocked ? <Notice tone="warn" testId="hide-upgrade">{t('portal.partners.brand.hideNeedsAddon')} <a href="/billing/upgrade">{t('portal.kit.upgrade')}</a></Notice> : null}
          <Button type="submit" variant="primary" disabled={problems.length > 0 || hideBlocked || save.pending}>{t('portal.partners.brand.save')}</Button>
        </form>
        <ErrorNote error={save.error} />
        {save.result ? <Notice tone="good">{t('portal.partners.brand.saved')}</Notice> : null}
      </Panel>
      <BrandPreview form={f} hide={hide && canHideBranding(initial)} />
    </div>
  );
}

/** Client portal preview. Only brand variables change (one shell, one theme key, QM-D02-003). */
export function BrandPreview({ form, hide }: { form: BrandForm; hide: boolean }) {
  const { t } = useSurface();
  const style = brandTokens(form.accentColor) as CSSProperties;
  return (
    <div className="qx-preview" style={style} data-testid="brand-preview" aria-label={t('portal.partners.brand.preview')}>
      <div className="qx-preview__bar">
        {form.logoUrl.startsWith('https://') ? <img className="qx-preview__logo" src={form.logoUrl} alt={form.portalName || t('portal.partners.brand.logo')} /> : null}
        <strong data-testid="preview-name">{form.portalName || t('portal.partners.brand.portalName')}</strong>
      </div>
      <Text>{t('portal.partners.brand.previewBody')}</Text>
      <button type="button" className="qx-preview__cta">{t('portal.partners.brand.previewCta')}</button>
      {form.supportEmail ? <Text tone="muted">{t('portal.partners.brand.support', { email: form.supportEmail })}</Text> : null}
      {hide ? null : <Text tone="muted" data-testid="powered-by">{t('portal.partners.brand.poweredBy')}</Text>}
    </div>
  );
}

function DomainsPanel() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const list = useResource('domains', api.domains);
  const [host, setHost] = useState('');
  const add = useAction(api.addDomain);
  const verify = useAction(api.verifyDomain);
  return (
    <div data-testid="domains">
      <form className="qx-toolbar" onSubmit={async (e) => { e.preventDefault(); if (await add.run(host.trim().toLowerCase())) { setHost(''); list.reload(); } }}>
        <Field label={t('portal.partners.domain.host')} hint={t('portal.partners.domain.hostHint')}>{(id) => <input id={id} name="hostname" value={host} onChange={(e) => setHost(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" disabled={!host.trim() || add.pending}>{t('portal.partners.domain.add')}</Button>
      </form>
      <ErrorNote error={add.error} />
      <Loadable resource={list}>
        {(rows) => rows.length === 0 ? <Notice>{t('portal.partners.domain.none')}</Notice> : <>{rows.map((d) => {
          const step = domainStep(d);
          return (
            <Panel key={d.id} data-domain={d.hostname}>
              <Heading level={2}>{d.hostname} <Badge tone={step === 'active' ? 'good' : step === 'failed' ? 'bad' : 'warn'} testId={`domain-${step}`}>{t(`portal.partners.domain.step.${step}`)}</Badge></Heading>
              {step !== 'active' ? (
                <DataTable rows={[{ type: 'CNAME', name: d.instructions.cname.name, value: d.instructions.cname.target || CNAME_TARGET }, { type: 'TXT', name: d.instructions.txt.name, value: d.instructions.txt.value }]} rowKey={(r) => r.type} empty="" columns={[
                  { key: 'type', header: t('portal.partners.domain.type'), render: (r) => r.type },
                  { key: 'name', header: t('portal.partners.domain.name'), render: (r) => <code>{r.name}</code> },
                  { key: 'value', header: t('portal.partners.domain.value'), render: (r) => <code>{r.value}</code> },
                ]} />
              ) : <Text>{t('portal.partners.domain.sslActive')}</Text>}
              {d.lastError ? <Notice tone="bad" testId="domain-error">{t(`portal.partners.domain.error.${d.lastError}`)}</Notice> : null}
              <Text tone="muted">{t('portal.partners.domain.txtRequired')}</Text>
              {step !== 'active' ? <Button onClick={async () => { if (await verify.run(d.id)) list.reload(); }} disabled={verify.pending}>{t('portal.partners.domain.verify')}</Button> : null}
            </Panel>
          );
        })}</>}
      </Loadable>
      <ErrorNote error={verify.error} />
    </div>
  );
}

/** True billing descriptor: agency brand on reports/emails, legal seller on invoices + payment pages (B08-003). */
export function DescriptorPanel() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [f, setF] = useState({ billingModel: 'quixo_billed' as 'quixo_billed' | 'agency_billed', legalName: '', billingEmail: '', currency: 'USD' as 'USD' | 'AED', descriptor: '' });
  const [surface, setSurface] = useState<BrandSurface>('report');
  const render = useResource(`render:${surface}`, () => api.renderSurface(surface));
  const save = useAction(api.saveDescriptor);
  const problem = f.billingModel === 'agency_billed' ? descriptorError(f.descriptor) : null;
  const effective = trueBillingDescriptor({ billingModel: f.billingModel, legalName: f.legalName || null, descriptor: f.descriptor || null });
  return (
    <div className="qx-grid" data-testid="descriptor">
      <Panel>
        <form onSubmit={async (e) => { e.preventDefault(); if (!problem) await save.run({ ...f, ...(f.descriptor ? {} : { descriptor: undefined }) }); }}>
          <Field label={t('portal.partners.billing.model')}>{(id) => (
            <select id={id} value={f.billingModel} onChange={(e) => setF((x) => ({ ...x, billingModel: e.target.value as typeof f.billingModel }))}>
              <option value="quixo_billed">{t('partners.onboarding.billingModel.quixo_billed')}</option>
              <option value="agency_billed">{t('partners.onboarding.billingModel.agency_billed')}</option>
            </select>
          )}</Field>
          <Field label={t('portal.partners.billing.legalName')}>{(id) => <input id={id} value={f.legalName} onChange={(e) => setF((x) => ({ ...x, legalName: e.target.value }))} />}</Field>
          <Field label={t('portal.partners.billing.email')}>{(id) => <input id={id} type="email" value={f.billingEmail} onChange={(e) => setF((x) => ({ ...x, billingEmail: e.target.value }))} />}</Field>
          {f.billingModel === 'agency_billed' ? (
            <Field label={t('partners.onboarding.descriptor')} error={problem ? t(`portal.partners.billing.error.${problem}`) : null}>
              {(id) => <input id={id} name="descriptor" maxLength={22} value={f.descriptor} onChange={(e) => setF((x) => ({ ...x, descriptor: e.target.value }))} />}
            </Field>
          ) : null}
          <Text data-testid="effective-descriptor">{t('portal.partners.billing.effective', { d: effective })}</Text>
          <Button type="submit" variant="primary" disabled={!!problem || !f.legalName || !f.billingEmail || save.pending}>{t('portal.partners.brand.save')}</Button>
        </form>
        <ErrorNote error={save.error} />
      </Panel>
      <Panel>
        <Field label={t('portal.partners.billing.surface')}>{(id) => <select id={id} value={surface} onChange={(e) => setSurface(e.target.value as BrandSurface)}>{BRAND_SURFACES.map((s) => <option key={s} value={s}>{t(`portal.partners.billing.surface.${s}`)}</option>)}</select>}</Field>
        <Loadable resource={render}>
          {(r) => (
            <>
              <Text>{t('portal.partners.billing.renderedDescriptor', { d: r.descriptor })}</Text>
              <iframe title={t('portal.partners.brand.preview')} sandbox="" srcDoc={r.html} style={{ inlineSize: '100%', blockSize: '240px', border: 'var(--q-rule)' }} />
            </>
          )}
        </Loadable>
      </Panel>
    </div>
  );
}
